import { fetcher } from 'utils/axios';

export interface Extension {
  id?: number;
  Id?: number; // SQL Server returns PascalCase
  userId?: string;
  UserId?: string;
  companyId?: string;
  CompanyId?: string;
  name?: string;
  Name?: string;
  url?: string;
  Url?: string;
  description?: string;
  Description?: string;
  isActive?: boolean;
  IsActive?: boolean;
  dateCreate?: string;
  DateCreate?: string;
  dateUpdate?: string;
  DateUpdate?: string;
}

export interface CreateExtensionData {
  name: string;
  url: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateExtensionData {
  name?: string;
  url?: string;
  description?: string;
  isActive?: boolean;
}

export interface ExtensionsResponse {
  success: boolean;
  data: Extension[];
  timestamp?: string;
}

export interface ExtensionResponse {
  success: boolean;
  data: Extension;
  timestamp?: string;
}

// Helper function to normalize extension data from server (handles PascalCase from SQL Server)
function normalizeExtension(ext: any): Extension {
  return {
    id: ext.Id ?? ext.id,
    Id: ext.Id ?? ext.id,
    userId: ext.UserId ?? ext.userId,
    UserId: ext.UserId ?? ext.userId,
    companyId: ext.CompanyId ?? ext.companyId,
    CompanyId: ext.CompanyId ?? ext.companyId,
    name: ext.Name ?? ext.name,
    Name: ext.Name ?? ext.name,
    url: ext.Url ?? ext.url,
    Url: ext.Url ?? ext.url,
    description: ext.Description ?? ext.description,
    Description: ext.Description ?? ext.description,
    isActive: ext.IsActive ?? ext.isActive,
    IsActive: ext.IsActive ?? ext.isActive,
    dateCreate: ext.DateCreate ?? ext.dateCreate,
    DateCreate: ext.DateCreate ?? ext.dateCreate,
    dateUpdate: ext.DateUpdate ?? ext.dateUpdate,
    DateUpdate: ext.DateUpdate ?? ext.dateUpdate,
  };
}

// Get all extensions for the current user
export async function fetchGetExtensions(): Promise<Extension[]> {
  try {
    const response = await fetcher('get', ['/api/extensions', {}], 'servernode') as ExtensionsResponse;
    if (response?.success && response?.data && Array.isArray(response.data)) {
      return response.data.map(normalizeExtension);
    }
    return [];
  } catch (error) {
    console.error('Error fetching extensions:', error);
    return [];
  }
}

// Get extension by ID
export async function fetchGetExtensionById(id: number): Promise<Extension | null> {
  try {
    const response = await fetcher('get', [`/api/extensions/${id}`, {}], 'servernode') as ExtensionResponse;
    if (response?.success && response?.data) {
      return normalizeExtension(response.data);
    }
    return null;
  } catch (error) {
    console.error('Error fetching extension by ID:', error);
    return null;
  }
}

// Create new extension
export async function fetchCreateExtension(data: CreateExtensionData): Promise<Extension | null> {
  try {
    const response = await fetcher('post', ['/api/extensions', { data }], 'servernode') as ExtensionResponse;
    if (response?.success && response?.data) {
      return normalizeExtension(response.data);
    }
    return null;
  } catch (error) {
    console.error('Error creating extension:', error);
    return null;
  }
}

// Update extension
export async function fetchUpdateExtension(id: number, data: UpdateExtensionData): Promise<Extension | null> {
  try {
    const response = await fetcher('put', [`/api/extensions/${id}`, { data }], 'servernode') as ExtensionResponse;
    if (response?.success && response?.data) {
      return normalizeExtension(response.data);
    }
    return null;
  } catch (error) {
    console.error('Error updating extension:', error);
    return null;
  }
}

// Delete extension
export async function fetchDeleteExtension(id: number): Promise<boolean> {
  try {
    const response = await fetcher('delete', [`/api/extensions/${id}`, {}], 'servernode') as { success: boolean; message?: string };
    return response?.success === true;
  } catch (error) {
    console.error('Error deleting extension:', error);
    return false;
  }
}

// Toggle extension active status
export async function fetchToggleExtensionActive(id: number, isActive: boolean): Promise<Extension | null> {
  try {
    const response = await fetcher('patch', [`/api/extensions/${id}/toggle`, { data: { isActive } }], 'servernode') as ExtensionResponse;
    if (response?.success && response?.data) {
      return normalizeExtension(response.data);
    }
    return null;
  } catch (error) {
    console.error('Error toggling extension active status:', error);
    return null;
  }
}

